import { Skeleton } from "frontend-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDot } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useYearlyCycle } from "./useYearlyCycle";
import type { TaskWithOccurrence } from "@dashboard/utils/taskWithOccurrence";
import { OCCURRENCE_STATUS_CONFIG } from "@dashboard/utils/occurrenceStatusConfig";
import { useNavigate } from "@tanstack/react-router";
import { indexToMonth } from "@dashboard/utils/months";

type TaskIndicatorProps = {
    id: number;
    title: string;
    task: TaskWithOccurrence;
};

export function TasksLoadingSkeleton() {
    return (
        <div>
            <Skeleton className="w-20 h-4 mb-4 rounded-full" />
            <ul className="flex flex-col gap-2">
                <li className="flex items-center gap-2 font-light">
                    <Skeleton className="w-4 h-4 rounded-full" />
                    <Skeleton className="h-4 w-32 rounded-full" />
                </li>
                <li className="flex items-center gap-2 font-light">
                    <Skeleton className="w-4 h-4 rounded-full" />
                    <Skeleton className="h-4 w-32 rounded-full" />
                </li>
                <li className="flex items-center gap-2 font-light">
                    <Skeleton className="w-4 h-4 rounded-full" />
                    <Skeleton className="h-4 w-32 rounded-full" />
                </li>
            </ul>
        </div>
    );
}

export function TaskIndicator({ id, title, task }: TaskIndicatorProps) {
    const navigate = useNavigate();
    const occurrenceStatus = task.occurrence?.status ?? "OPEN";
    const isExpired = task.isExpired;
    const dotColor = isExpired
        ? "var(--color-chameleon-pink)"
        : OCCURRENCE_STATUS_CONFIG[occurrenceStatus].dot;

    const openTask = () => {
        void navigate({
            to: ".",
            search: (prev) => ({ ...prev, task: id, edit: undefined }),
        });
    };

    return (
        <li className="flex items-start gap-2 font-light">
            <span className="h-lh flex items-center shrink-0">
                <FontAwesomeIcon
                    icon={faDot}
                    className="text-4xl max-w-4"
                    style={{ color: dotColor }}
                />
            </span>
            <button
                type="button"
                onClick={openTask}
                className={`hover:underline cursor-pointer line-clamp-2 text-left${
                    isExpired ? " text-chameleon-pink" : ""
                }`}
            >
                {title}
            </button>
        </li>
    );
}

export function TaskSection({ month, tasks }: { month: number; tasks: TaskWithOccurrence[] }) {
    const { t } = useTranslation();
    const { active } = useYearlyCycle();

    return (
        <div key={month} className={`${!active.has(month) ? "sr-only" : ""}`}>
            <h1 className="font-medium mb-2">
                {t(`common.month.long.${indexToMonth[month + 1]}`)}
            </h1>
            {tasks.length === 0 && (
                <p className="font-light text-sm italic text-text-secondary">
                    {t("common.noActivities")}
                </p>
            )}
            {tasks.length > 0 && (
                <div>
                    <ul className="flex flex-col gap-1">
                        {tasks.map((task) => {
                            return (
                                <TaskIndicator
                                    key={task.id}
                                    id={task.id}
                                    title={task.title || ""}
                                    task={task}
                                />
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}
